<template>
  <CommonLayout>
    <div v-if="activeMenu === 'dashboard'" class="dashboard-content">
      <!-- 新增卡片网格布局 -->
      <div class="card-grid">
        <!-- 通行密钥卡片 -->
        <el-card class="dashboard-card" shadow="hover">
          <template #header>
            <div class="card-header">
              <el-icon><Key /></el-icon>
              <!-- 修改标题 -->
              <span>通行密钥(accessKey)</span>
            </div>
          </template>
          <div class="card-content">
            <p class="card-text">管理您的通行密钥，用于 API 访问认证</p>
            <div v-if="accessKey === ''">
              <el-button type="primary" class="card-button" @click="generateAccessKey" :loading="loading">
                生成
              </el-button>
            </div>
            <div v-else class="access-key-display">
              <span>{{ accessKey }}</span>
              <el-icon @click="copyAccessKey" class="copy-icon" color="#409EFF"><DocumentCopy /></el-icon>
            </div>
          </div>
        </el-card>
        <!-- Object 密钥卡片 -->
        <el-card class="dashboard-card" shadow="hover">
          <template #header>
            <div class="card-header">
              <el-icon><Lock /></el-icon>
              <!-- 修改标题 -->
              <span>Object密钥(objectKey)</span>
            </div>
          </template>
          <div class="card-content">
            <p class="card-text">管理您的 Object 密钥，用于对象存储访问</p>
            <div v-if="objectKey === ''">
              <el-button type="primary" class="card-button" @click="openSetObjectKeyDialog">
                管理
              </el-button>
            </div>
            <div v-else class="object-key-display">
              <span>{{ objectKey }}</span>
              <el-icon @click="copyObjectKey" class="copy-icon" color="#409EFF"><DocumentCopy /></el-icon>
              <el-icon @click="openSetObjectKeyDialog" class="edit-icon" color="#fb8e19"><Edit /></el-icon>
            </div>
          </div>
        </el-card>
      </div>
    </div>
    <router-view v-else></router-view>
    <el-dialog v-model="setObjectKeyDialogVisible" title="设置 Object 密钥" width="35%">
      <el-form :model="newObjectKeyForm">
        <el-form-item label="Object 密钥">
          <el-input v-model="newObjectKeyForm.objectKey"></el-input>
        </el-form-item>
        <div class="rule-tip">
          ObjectKey 应为 8 - 32 位字符串，且只能包含字母、数字和斜杠，不能以斜杠开头或结尾。
        </div>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="setObjectKeyDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="setObjectKey">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </CommonLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { DocumentCopy, Edit, Key, Lock } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { generateTimestamp, generateToken } from '@/utils/crypto'
import { API_PATHS } from '@/config/api'
import CommonLayout from '@/components/CommonLayout.vue'
import service from '@/utils/http'; // 引入新的 axios 实例

// const route = useRoute()
const router = useRouter()
const activeMenu = ref('dashboard')

// 从 localStorage 中获取 userInfo
const userInfoStr = localStorage.getItem('userInfo')
const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null
const userId = ref(userInfo ? userInfo.userId : null)

const accessKey = ref('')
const objectKey = ref('') 
const loading = ref(false)
const setObjectKeyDialogVisible = ref(false)
const newObjectKeyForm = ref({ objectKey: '' })

const getAccessKey = async () => {
  loading.value = true
  try {
    const timestamp = generateTimestamp()
    const params = {
      userId: userId.value, 
      timestamp,
      token: generateToken({ userId: userId.value, timestamp }), 
    }
    // 修改部分：将 axios.get 替换为 service.get
    const response = await service.get(`${API_PATHS.accessKey}`, {
      params,
    })
    accessKey.value = response.data
  } catch (error) {
    ElMessage.error(error.message || '获取通行密钥失败')
  } finally {
    loading.value = false
  }
}

const generateAccessKey = async () => {
  loading.value = true;
  try {
    const timestamp = generateTimestamp();
    const params = {
      userId: userId.value, 
      timestamp,
      token: generateToken({ userId: userId.value, timestamp }), 
    };
    // 使用新的 axios 实例发送请求
    const response = await service.post(API_PATHS.generateAccessKey, params);
    accessKey.value = response.data;
  } catch (error) {
    // 错误处理已经在拦截器中完成
  } finally {
    loading.value = false;
  }
};

const copyAccessKey = async () => {
  try {
    await navigator.clipboard.writeText(accessKey.value)
    ElMessage.success('复制成功')
  } catch (error) {
    ElMessage.error('复制失败')
  }
}

const getObjectKey = async () => {
  try {
    const timestamp = generateTimestamp()
    const params = {
      userId: userId.value,
      timestamp,
      token: generateToken({ userId: userId.value, timestamp }),
    }
    const response = await service.get(`${API_PATHS.objectKey}`, {
      params,
    })
    objectKey.value = response.data
  } catch (error) {
    ElMessage.error(error.message || '获取 Object 密钥失败')
  }
}

const copyObjectKey = async () => {
  try {
    await navigator.clipboard.writeText(objectKey.value)
    ElMessage.success('复制成功')
  } catch (error) {
    ElMessage.error('复制失败')
  }
}

const openSetObjectKeyDialog = () => {
  newObjectKeyForm.value.objectKey = objectKey.value;
  setObjectKeyDialogVisible.value = true;
}

const setObjectKey = async () => {
  try {
    const timestamp = generateTimestamp()
    const objectKeyParam = newObjectKeyForm.value.objectKey;
    const params = {
      userId: userId.value,
      timestamp,
      objectKey: objectKeyParam
    }
    const token = generateToken({ 
      userId: userId.value, 
      timestamp, 
      objectKey: objectKeyParam 
    });
    params.token = token;

    await service.post(`${API_PATHS.setObjectKey}`, params)
    objectKey.value = newObjectKeyForm.value.objectKey
    setObjectKeyDialogVisible.value = false
    ElMessage.success('Object 密钥设置成功')
  } catch (error) {
    ElMessage.error(error.message || '设置 Object 密钥失败')
  }
}

onMounted(() => {
  if (!userInfo) {
    ElMessage.error('未获取到用户信息，请重新登录')
    router.push('/auth') 
  } else {
    getAccessKey()
    getObjectKey() 
  }
})
</script>

<style>
/* 新增卡片网格布局样式 */
.card-grid {
  display: flex; 
  flex-wrap: wrap; 
  gap: 20px; 
  max-width: 1200px; 
  margin: 0 auto; 
  /* 增加卡片与顶部的距离 */
  margin-top: 50px; 
}

.dashboard-card {
  flex: 1; 
  min-width: calc(50% - 10px); 
  height: 200px; 
}

.card-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(100% - 50px);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
}

.card-header .el-icon {
  font-size: 20px;
  color: #409EFF;
}

.card-content p {
  color: #606266;
  margin: 0;
  line-height: 1.6;
}

.card-button {
  align-self: flex-start;
  margin-top: 16px;
}

.access-key-display,
.object-key-display {
  margin-top: 20px;
  display: flex;
  align-items: center;
}

.access-key-display span,
.object-key-display span {
  color: #858382;
  font-size: 0.9em;
}

.copy-icon,
.edit-icon {
  margin-left: 10px;
  cursor: pointer;
}
</style>
